import type {
  ActualEntry,
  Breakdown,
  Codes,
  Deliverable,
  Frozen,
  FrozenDeliverable,
  IndirectItem,
  Period,
  Series,
  Settings,
  Unit,
} from "./types";
import { LOE_CHAIN, SERIES } from "./types";
import { bookSeries, weightAt, currentWeight } from "./series";
import { stepShares } from "./chains";
import { spanPeriods } from "./loe";
import { applyAutoForecast } from "./autoforecast";

export interface ProjectData {
  periods: Period[];
  deliverables: Deliverable[];
  actuals: ActualEntry[];
  indirects: IndirectItem[];
  settings: Settings;
  frozen?: Frozen;
  /** Index of the current (open) period, i.e. the reporting cutoff. */
  currentIdx: number;
  /** Position of each code in its breakdown's set order (Cesar, Nov 1: "E, P, C, CM"). Codes without one sort after, by code. */
  codeOrder?: CodeOrder;
}

export type CodeOrder = Partial<Record<Breakdown, Map<string, number>>>;

/** Compare two codes of one breakdown: by the breakdown's set order, then by code (numbers in numeric order). */
export function compareCodes(order: CodeOrder | undefined, b: Breakdown, x: string, y: string): number {
  const r = order?.[b];
  const rx = r?.get(x) ?? Infinity;
  const ry = r?.get(y) ?? Infinity;
  if (rx !== ry) return rx < ry ? -1 : 1;
  return x.localeCompare(y, undefined, { numeric: true });
}

/** Compare two code tuples, breakdown by breakdown (`by` gives the breakdown of each position). */
export function compareKeys(order: CodeOrder | undefined, by: Breakdown[], x: string[], y: string[]): number {
  for (let i = 0; i < by.length; i++) {
    const c = compareCodes(order, by[i], x[i] ?? "", y[i] ?? "");
    if (c) return c;
  }
  return 0;
}

interface PreparedDeliverable {
  d: Deliverable;
  unit: Unit;
  shares: number[];
  /** Merged codes per step: step codes override the deliverable's (rule 4). */
  stepCodes: Codes[];
  weights: Float64Array;
  book: Record<Series, { bookings: { periodIdx: number; stepIdx: number; value: number }[]; reachedByPeriod: Int32Array }>;
  derivedForecast: number[];
}

export interface Prepared {
  data: ProjectData;
  items: PreparedDeliverable[];
  indirectSpread: { item: IndirectItem; perPeriod: Float64Array }[];
}

function mergeCodes(base: Codes, over?: Codes): Codes {
  if (!over) return base;
  const out: Codes = { ...base };
  for (const [k, v] of Object.entries(over)) if (v) out[k as Breakdown] = v;
  return out;
}

/** Rule 20 (R37): indirect budget spread evenly per period between its start and end. */
export function spreadIndirect(item: IndirectItem, periods: Period[]): Float64Array {
  const out = new Float64Array(periods.length);
  const r = spanPeriods(periods, item.start, item.end);
  if (!r) return out;
  const count = r.to - r.from + 1;
  for (let i = r.from; i <= r.to; i++) out[i] = item.budget / count;
  return out;
}

export function prepare(data: ProjectData): Prepared {
  const { periods, frozen, settings } = data;
  const items: PreparedDeliverable[] = data.deliverables.map((orig) => {
    let d = orig;
    let derived: number[] = [];
    // LOE has no forecast dates to derive: it follows its plan.
    if (settings.autoForecast && orig.chainCode !== LOE_CHAIN) {
      const r = applyAutoForecast(orig, periods, data.currentIdx, settings.autoForecastSpacing);
      d = r.deliverable;
      derived = r.derived;
    }
    const weights = new Float64Array(periods.length);
    for (let p = 0; p < periods.length; p++) weights[p] = weightAt(d, periods, p);
    const fz: FrozenDeliverable | undefined = frozen?.deliverables[d.id];
    const book = {} as PreparedDeliverable["book"];
    for (const s of SERIES)
      book[s] = bookSeries(d, periods, s, fz && frozen ? { lastClosedIdx: frozen.lastClosedIdx, state: fz } : undefined);
    return {
      d,
      unit: d.unit ?? "h",
      shares: stepShares(d.steps),
      stepCodes: d.steps.map((s) => mergeCodes(d.codes, s.codes)),
      weights,
      book,
      derivedForecast: derived,
    };
  });
  const indirectSpread = data.indirects.map((item) => ({ item, perPeriod: spreadIndirect(item, periods) }));
  return { data, items, indirectSpread };
}

export type CodeFilter = Partial<Record<Breakdown, string[]>>;

function passes(codes: Codes, f?: CodeFilter): boolean {
  if (!f) return true;
  for (const [k, vals] of Object.entries(f)) {
    if (!vals || !vals.length) continue;
    if (!vals.includes(codes[k as Breakdown] ?? "")) return false;
  }
  return true;
}

export interface Agg {
  key: string[];
  /** Budget in effect per period (level, not increment). */
  bac: Float64Array;
  /** Per-period increments. */
  pv: Float64Array;
  ev: Float64Array;
  fv: Float64Array;
  ac: Float64Array;
  /** Indirect layer (rule 20): planned spread, actuals, budget. */
  pvInd: Float64Array;
  acInd: Float64Array;
  bacInd: number;
  deliverables: Set<string>;
}

function newAgg(key: string[], n: number): Agg {
  return {
    key,
    bac: new Float64Array(n),
    pv: new Float64Array(n),
    ev: new Float64Array(n),
    fv: new Float64Array(n),
    ac: new Float64Array(n),
    pvInd: new Float64Array(n),
    acInd: new Float64Array(n),
    bacInd: 0,
    deliverables: new Set(),
  };
}

export interface GroupOptions {
  by: Breakdown[];
  unit?: Unit;
  filter?: CodeFilter;
}

/**
 * Rule 10: roll-ups are plain sums of deliverable values grouped by any combination of breakdowns. No weight factoring.
 * Rule 12: actual hours come from the discipline-level entries, grouped by their own allocation codes; a breakdown an
 * entry isn't allocated to groups under "".
 */
export function group(prep: Prepared, opts: GroupOptions): { groups: Agg[]; total: Agg } {
  const { periods } = prep.data;
  const n = periods.length;
  const unit = opts.unit ?? "h";
  const map = new Map<string, Agg>();
  const total = newAgg([], n);
  const get = (codes: Codes) => {
    const key = opts.by.map((b) => codes[b] ?? "");
    const k = key.join("\u0001");
    let a = map.get(k);
    if (!a) map.set(k, (a = newAgg(key, n)));
    return a;
  };

  for (const it of prep.items) {
    if (it.unit !== unit) continue;
    const shareByAgg = new Map<Agg, number>();
    it.stepCodes.forEach((c, j) => {
      if (!passes(c, opts.filter)) return;
      const a = get(c);
      shareByAgg.set(a, (shareByAgg.get(a) ?? 0) + it.shares[j]);
      a.deliverables.add(it.d.id);
      total.deliverables.add(it.d.id);
    });
    for (const [a, share] of shareByAgg) {
      for (let p = 0; p < n; p++) {
        const v = it.weights[p] * share;
        a.bac[p] += v;
        total.bac[p] += v;
      }
    }
    for (const s of SERIES) {
      const arrKey = s === "planned" ? "pv" : s === "earned" ? "ev" : "fv";
      for (const b of it.book[s].bookings) {
        const c = it.stepCodes[b.stepIdx];
        if (!passes(c, opts.filter)) continue;
        get(c)[arrKey][b.periodIdx] += b.value;
        total[arrKey][b.periodIdx] += b.value;
      }
    }
  }

  if (unit === "h") {
    for (const e of prep.data.actuals) {
      if (!passes(e.codes, opts.filter)) continue;
      const idx = periods.findIndex((p) => p.number === e.periodNumber);
      if (idx < 0) continue;
      const a = get(e.codes);
      if (e.indirect) {
        a.acInd[idx] += e.hours;
        total.acInd[idx] += e.hours;
      } else {
        a.ac[idx] += e.hours;
        total.ac[idx] += e.hours;
      }
    }
    for (const { item, perPeriod } of prep.indirectSpread) {
      if (!passes(item.codes, opts.filter)) continue;
      const a = get(item.codes);
      a.bacInd += item.budget;
      total.bacInd += item.budget;
      for (let p = 0; p < n; p++) {
        a.pvInd[p] += perPeriod[p];
        total.pvInd[p] += perPeriod[p];
      }
    }
  }

  const groups = [...map.values()].sort((x, y) => compareKeys(prep.data.codeOrder, opts.by, x.key, y.key));
  return { groups, total };
}

const div = (a: number, b: number): number | null => (b === 0 ? null : a / b);

function cum(arr: Float64Array, i: number): number {
  let s = 0;
  for (let k = 0; k <= i && k < arr.length; k++) s += arr[k];
  return s;
}

export interface Metrics {
  periodIdx: number;
  bac: number;
  pv: number;
  ev: number;
  fv: number;
  ac: number;
  pvPeriod: number;
  evPeriod: number;
  fvPeriod: number;
  acPeriod: number;
  /** Rule 9. */
  pctPlanned: number | null;
  pctEarned: number | null;
  pctForecast: number | null;
  pctExpended: number | null;
  sv: number;
  cv: number;
  /** Rule 13. */
  spi: number | null;
  cpi: number | null;
  csi: number | null;
  spiPeriod: number | null;
  cpiPeriod: number | null;
  /** Rule 14. */
  etc: number;
  eac1: number | null;
  eacBasic: number;
  vac: number | null;
  vacBasic: number;
  tcpi: number | null;
}

/**
 * Metrics for a group at a period index, computed from the group's own sums (rule 14: subtotals never add child EACs).
 * Indirects join PV/EV/AC/BAC only when settings.includeIndirects is on (rule 13 toggle, open Q4 for their EV).
 */
export function metricsAt(a: Agg, i: number, settings: Settings): Metrics {
  const inc = settings.includeIndirects;
  const pvIndCum = cum(a.pvInd, i);
  const acIndCum = cum(a.acInd, i);
  const evInd = settings.indirectEarned === "actual" ? acIndCum : pvIndCum;
  const evIndPer = settings.indirectEarned === "actual" ? a.acInd[i] ?? 0 : a.pvInd[i] ?? 0;
  const bac = (a.bac[i] ?? 0) + (inc ? a.bacInd : 0);
  const pv = cum(a.pv, i) + (inc ? pvIndCum : 0);
  const ev = cum(a.ev, i) + (inc ? evInd : 0);
  const fv = cum(a.fv, i) + (inc ? pvIndCum : 0);
  const ac = cum(a.ac, i) + (inc ? acIndCum : 0);
  const pvPeriod = (a.pv[i] ?? 0) + (inc ? a.pvInd[i] ?? 0 : 0);
  const evPeriod = (a.ev[i] ?? 0) + (inc ? evIndPer : 0);
  const fvPeriod = (a.fv[i] ?? 0) + (inc ? a.pvInd[i] ?? 0 : 0);
  const acPeriod = (a.ac[i] ?? 0) + (inc ? a.acInd[i] ?? 0 : 0);
  const spi = div(ev, pv);
  const cpi = div(ev, ac);
  const etc = bac - ev;
  const eac1 = cpi === null || cpi === 0 ? null : etc / cpi + ac;
  const eacBasic = etc + ac;
  return {
    periodIdx: i,
    bac,
    pv,
    ev,
    fv,
    ac,
    pvPeriod,
    evPeriod,
    fvPeriod,
    acPeriod,
    pctPlanned: div(pv, bac),
    pctEarned: div(ev, bac),
    pctForecast: div(fv, bac),
    pctExpended: div(ac, bac),
    sv: ev - pv,
    cv: ev - ac,
    spi,
    cpi,
    csi: spi === null || cpi === null ? null : spi * cpi,
    spiPeriod: div(evPeriod, pvPeriod),
    cpiPeriod: div(evPeriod, acPeriod),
    etc,
    eac1,
    eacBasic,
    vac: eac1 === null ? null : bac - eac1,
    vacBasic: bac - eacBasic,
    tcpi: div(bac - ev, bac - ac),
  };
}

/** Rule 19: headcount = period hours / that period's standard hours. */
export function histogram(a: Agg, periods: Period[], settings: Settings) {
  return periods.map((p, i) => {
    const m = metricsAt(a, i, settings);
    const h = p.stdHours || NaN;
    return {
      period: p,
      planned: m.pvPeriod / h,
      earned: m.evPeriod / h,
      forecast: m.fvPeriod / h,
      actual: m.acPeriod / h,
    };
  });
}

/** Per-deliverable status at a period index, for registers and work-item lists. */
export function deliverableStatus(prep: Prepared, i: number) {
  return prep.items.map((it) => {
    const w = it.weights[i] ?? currentWeight(it.d);
    const cumOf = (s: Series) => it.book[s].bookings.reduce((t, b) => (b.periodIdx <= i ? t + b.value : t), 0);
    const reached = (s: Series) => it.book[s].reachedByPeriod[i] ?? 0;
    const pv = cumOf("planned");
    const ev = cumOf("earned");
    const fv = cumOf("forecast");
    const re = reached("earned");
    return {
      id: it.d.id,
      code: it.d.code,
      weight: w,
      unit: it.unit,
      pv,
      ev,
      fv,
      pctPlanned: w ? pv / w : null,
      pctEarned: w ? ev / w : null,
      pctForecast: w ? fv / w : null,
      lastEarned: re > 0 ? it.d.steps[re - 1].milestone : null,
      nextMilestone: re < it.d.steps.length ? it.d.steps[re].milestone : null,
      derivedForecast: it.derivedForecast,
      forecastSteps: it.d.steps.map((s) => s.forecast ?? null),
    };
  });
}

/** Rule 16: snapshot the booked state through a period so later computations never restate it. */
export function freezeThrough(prep: Prepared, idx: number): Frozen {
  const deliverables: Record<string, FrozenDeliverable> = {};
  for (const it of prep.items) {
    const bookings = {} as FrozenDeliverable["bookings"];
    const reached = {} as FrozenDeliverable["reached"];
    for (const s of SERIES) {
      bookings[s] = it.book[s].bookings.filter((b) => b.periodIdx <= idx);
      reached[s] = it.book[s].reachedByPeriod[idx] ?? 0;
    }
    deliverables[it.d.id] = { bookings, reached };
  }
  return { lastClosedIdx: idx, deliverables };
}

export function cumulativeSeries(a: Agg, settings: Settings, upto?: number) {
  const n = upto === undefined ? a.bac.length : Math.min(upto + 1, a.bac.length);
  const out: Metrics[] = [];
  for (let i = 0; i < n; i++) out.push(metricsAt(a, i, settings));
  return out;
}
